import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Zap, Shield, TrendingUp, ScanLine, CheckCircle2 } from 'lucide-react';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns a structured 403 for unverified accounts. Redirect
      // the user to /verify-email so they can enter the code without
      // needing another sign-up.
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        toast.error(detail.message || 'Please verify your email');
        navigate(`/verify-email?email=${encodeURIComponent(detail.email || email)}`);
        return;
      }
      const message = typeof detail === 'string' ? detail : detail?.message;
      toast.error(message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left - Marketing Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950">
        {/* Slow-zoom background */}
        <img
          src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
          alt="Scania fleet truck"
          className="absolute inset-0 w-full h-full object-cover opacity-70 hero-bg-zoom"
          data-testid="hero-background"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/65 to-slate-950/95" />
        {/* Grid noise overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Top: Brand + live status */}
          <div className="flex items-center justify-between hero-anim-1">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-white rounded-sm flex items-center justify-center overflow-hidden">
                <img src="/fleetcost-icon-192.png" alt="FleetCost.App" className="w-9 h-9 object-contain" data-testid="login-brand-icon" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-display font-black text-2xl tracking-tight" data-testid="login-brand-name">
                  FleetCost<span className="text-blue-400">.App</span>
                </span>
                <span className="text-[10px] font-mono text-slate-400 tracking-[0.25em] uppercase mt-1">Fleet Operating System</span>
              </div>
            </div>
            <div className="flex items-center gap-2 border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 rounded-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 hero-live-dot" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300">Live in ZA</span>
            </div>
          </div>

          {/* Middle: Headline + copy + feature strip */}
          <div className="max-w-md">
            <p className="text-xs font-mono text-blue-400 tracking-[0.2em] uppercase mb-4 hero-anim-2" data-testid="hero-eyebrow">
              [ Mission Control / v2 ]
            </p>
            <h1 className="font-display text-5xl xl:text-6xl font-black mb-6 tracking-tight leading-[0.95] hero-anim-2" data-testid="hero-headline">
              <span className="hero-shine-text">Every kilometre.</span><br />
              <span className="hero-shine-text">Every litre.</span><br />
              Accounted for.
            </h1>
            <p className="text-slate-300 text-base leading-relaxed max-w-sm hero-anim-3" data-testid="hero-subheadline">
              The Swiss-precision fleet costing platform built for South African operators.
              Real-time cost tracking, AI-powered invoice capture, compliance at a glance.
            </p>

            {/* Feature callouts (icon strip) */}
            <div className="mt-8 space-y-2.5 hero-anim-4" data-testid="hero-features">
              {[
                { icon: ScanLine, label: 'AI invoice scanner', detail: 'Snap → extract → done' },
                { icon: TrendingUp, label: 'Fuel efficiency (km/L)', detail: 'Auto-calculated per vehicle' },
                { icon: Shield, label: 'License & compliance alerts', detail: 'Never miss an expiry' },
              ].map(({ icon: Icon, label, detail }) => (
                <div key={label} className="flex items-start gap-3 py-2 border-l-2 border-blue-500/60 pl-3">
                  <Icon className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-semibold text-white">{label}</span>
                    <span className="text-[11px] text-slate-400 font-mono">{detail}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* KPI strip */}
            <div className="mt-8 grid grid-cols-3 gap-0 border border-white/10 hero-anim-5" data-testid="hero-kpis">
              <div className="p-4 border-r border-white/10">
                <p className="font-display text-3xl font-black tabular-nums">98<span className="text-blue-400">%</span></p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-1">Cost visibility</p>
              </div>
              <div className="p-4 border-r border-white/10">
                <p className="font-display text-3xl font-black tabular-nums">40<span className="text-blue-400">%</span></p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-1">Less downtime</p>
              </div>
              <div className="p-4">
                <p className="font-display text-3xl font-black tabular-nums">2<span className="text-blue-400">hrs</span></p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-1">Saved weekly</p>
              </div>
            </div>
          </div>

          {/* Bottom: Trust marquee + copyright */}
          <div className="space-y-4 hero-anim-6">
            <div className="overflow-hidden border-y border-white/10 py-2.5">
              <div className="hero-marquee-track">
                {[
                  'Bakkies', 'Delivery vans', 'Long-haul trucks', 'Forklifts', 'Trailers',
                  'Excavators', 'Generators', 'Company cars', 'Buses', 'Motorcycles',
                  'Bakkies', 'Delivery vans', 'Long-haul trucks', 'Forklifts', 'Trailers',
                  'Excavators', 'Generators', 'Company cars', 'Buses', 'Motorcycles',
                ].map((label, i) => (
                  <span key={i} className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500 shrink-0">
                    <CheckCircle2 className="w-3 h-3 text-blue-400" />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-slate-500 text-[10px] font-mono tracking-[0.2em] uppercase">
                © 2026 FleetCost.App — Built for fleet managers
              </p>
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                <Zap className="w-3 h-3 text-blue-400" />
                <span>Made in South Africa</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/fleetcost-logo.png" alt="FleetCost.App" className="h-10 object-contain" data-testid="login-mobile-logo" />
          </div>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-8 pt-8 pb-6 border-b border-slate-100">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                [ Authenticate ]
              </p>
              <h2 className="font-display text-3xl font-black text-slate-950 tracking-tight">
                Sign in
              </h2>
              <p className="text-slate-500 text-sm mt-2">
                Access your fleet operations command centre.
              </p>
            </div>
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="label-eyebrow">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.co.za"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="label-eyebrow">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-[11px] font-mono uppercase tracking-wider text-slate-600 hover:text-slate-900 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Authenticating…' : 'Sign in →'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <Link
                  to="/forgot-username"
                  className="text-[11px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-900 hover:underline"
                  data-testid="forgot-username-link"
                >
                  Forgot your username?
                </Link>
              </div>
              <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <p className="text-sm text-slate-500">
                  New to FleetCost.App?{' '}
                  <Link
                    to="/register"
                    className="text-slate-950 font-semibold hover:underline"
                    data-testid="register-link"
                  >
                    Create account →
                  </Link>
                </p>
                <p className="mt-4 text-[11px] text-slate-400">
                  <Link to="/terms" className="hover:text-slate-700 hover:underline" data-testid="login-terms-link">Terms of Service</Link>
                  <span className="mx-2 text-slate-300">·</span>
                  <Link to="/privacy" className="hover:text-slate-700 hover:underline" data-testid="login-privacy-link">Privacy Policy</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
